namespace $ {
	export class $shm_hitalama_guide_content_interface extends $mol_object {
		static pages(): Record<string, $shm_hitalama_guide_content_page> {
			const page = this.$.$shm_hitalama_guide_content_page
			return {
				'interface-keyboard': page('Клавиатура', 'folder-interface', `Клавиши ниже относятся к работе с картой и стандартным настройкам. Перед командой карты выйдите из текстового поля: при вводе текста копирование, вставка и отмена обычно относятся к редактору. Буквы инструментов соответствуют физическим клавишам латинской раскладки и работают также при русской раскладке.

## Основные сочетания

| Клавиши | Действие |
| --- | --- |
| Ctrl+Shift+P / Cmd+Shift+P | Открыть палитру команд: найти действие по названию. |
| Ctrl+Z / Cmd+Z | Отменить последнее действие с картой. |
| Ctrl+Shift+Z / Cmd+Shift+Z | Повторить отменённое действие. |
| Ctrl+Y / Cmd+Y | Другой способ повторить отменённое действие. |
| Ctrl+C / Cmd+C | Копировать выделенные виджеты, если не выделен текст. |
| Ctrl+V / Cmd+V | Вставить на карту содержимое буфера: скопированные виджеты, текст или изображение. |
| Delete | Удалить выбранные виджеты вне поля ввода. |
| Shift + клик по виджету | Добавить виджет к текущему выделению. |
| Ctrl+G | Объединить выделенные объекты в группу. |
| Ctrl+Shift+G | Разгруппировать выбранную группу. |
| Ctrl+K / Cmd+K | Открыть поле URL ссылки выбранного виджета. |
| Ctrl + перетаскивание | Перемещать виджет за содержимое в стандартном режиме перетаскивания. |
| Escape | Закрыть палитру команд; выйти из полноэкранного виджета при стандартной настройке выхода. |

Ctrl в строках перетаскивания и группировки указан именно как Ctrl, в том числе на Mac. Для остальных строк вариант Cmd приведён только там, где он поддерживается приложением.

## Выбрать инструмент карты

| Клавиша | Инструмент |
| --- | --- |
| S | Выбор: работа с существующими объектами. |
| P | Свободное рисование. |
| R | Прямоугольник. |
| C | Круг — с оговоркой ниже. |
| T | Текст. |
| M | Markdown. |

Активный инструмент выделен на плавающей панели. Его повторное включение может вернуть режим выбора; чтобы явно закончить рисование, нажмите S или кнопку со стрелкой.

Клавиша **C** по умолчанию занята глобальным назначением собственных сочетаний. Пока эта функция включена и использует C, круг выбирайте кнопкой на панели. Чтобы освободить C для круга, измените клавишу назначения в окне настройки сочетания.

## Назначить сочетание кнопке

1. Вне текстового поля зажмите C и нажмите нужную кнопку или ссылку. Вместо выполнения действия откроется окно назначения.
2. Отпустите C и нажмите желаемую клавишу или сочетание.
3. Рядом с кнопкой появится подсказка. Сочетание сработает, когда эта кнопка доступна на экране.

Повторный C-клик открывает переназначение. Кнопка удаления или Delete в этом окне убирает привязку; Escape отменяет настройку. Там же можно изменить клавишу назначения. Привязки сохраняются в этом браузере отдельно для приложения; на другом устройстве их нужно настроить заново. Если окно назначения не появляется, эта возможность может быть недоступна в вашем браузере.

## Настройки и режим просмотра

В **Настройки → Горячие клавиши** можно изменить клавишу перетаскивания, выхода из fullscreen и включения режима просмотра. В режиме просмотра стрелки влево/вправо и PageUp/PageDown переключают кадры. Клавиша включения просмотра по умолчанию не назначена.

[Виджеты на карте](maps-widgets.md) · [Группы](maps-layout.md)`),
				'interface-mouse': page('Мышь', 'folder-interface', `Главное различие — какой инструмент выбран на плавающей панели карты. В режиме **Выбор** мышь работает с готовыми объектами. При выборе карандаша или фигуры следующее нажатие левой кнопки начинает рисование.

## Полотно, меню и масштаб

| Действие | Результат |
| --- | --- |
| Зажать правую кнопку и вести мышь | Переместить карту. Положение самих виджетов не меняется. |
| Коротко нажать правую кнопку на пустом фоне | Открыть меню добавления виджетов. |
| Правая кнопка на виджете | Открыть меню действий этого виджета. |
| Колесо над пустым фоном | Сдвинуть полотно по вертикали. |
| Shift + колесо | Сдвинуть полотно по горизонтали. |
| Ctrl + колесо | Изменить масштаб вокруг указателя. |
| Клик на пустом фоне | Снять выделение и закрыть открытое контекстное меню. |

В режиме рисования короткий ПКМ по полотну возвращает режим выбора; для добавления объекта через меню сначала выберите стрелку. Зажатая правая кнопка с движением продолжает служить для перемещения карты.

Над редактором, таблицей или длинным текстом колесо может прокручивать содержимое виджета. Для движения именно полотна наведите указатель на пустое место. Если камера не движется, проверьте **Заблокировать камеру** в меню карты. Кнопка с процентом масштаба в меню карты возвращает 100% и начальное положение.

## Выделить, передвинуть и изменить размер

- Клик по виджету выбирает его. Поле ввода или кнопка внутри могут сразу выполнить своё действие.
- Чтобы выбрать несколько объектов, протяните рамку левой кнопкой по пустому фону. Shift-клик добавляет объект к выделению. После выделения нажмите кнопку группы над рамкой или Ctrl+G.
- Для перемещения за содержимое удерживайте настроенную клавишу перетаскивания — по умолчанию Ctrl. Для явной ручки перетаскивания зажимать Ctrl не требуется.
- Чтобы изменить размер, тяните ручки на границе выбранного объекта. Точные координаты и размеры доступны в **Параметрах виджета**.

Если вместо редактирования текста двигается виджет, отпустите Ctrl или другую назначенную клавишу перетаскивания. Блокировка виджета защищает его положение от случайного перемещения.

## Рисовать и размещать текст

Выберите карандаш, прямоугольник или круг, зажмите левую кнопку в начальной точке и ведите мышь. Отпускание завершает рисунок. Прямоугольник и круг можно также поставить одним кликом: будет использован стандартный размер. Для текста или Markdown выберите соответствующий инструмент и нажмите в месте размещения.

Готовые штрихи и фигуры остаются видимыми, пока создаются сохранённые виджеты. Для работы с ними вернитесь к стрелке **Выбор**. Саму плавающую панель перемещайте за ручку с точками на её краю.

[Управление картой](maps-navigation.md) · [Виджеты](maps-widgets.md) · [История изменений](maps-history.md)`),
				'interface-trackpad': page('Тачпад', 'folder-interface', `Тачпад ноутбука управляет указателем и передаёт клики и прокрутку, как мышь. Сенсорный экран планшета передаёт касания непосредственно в карту: его жесты описаны отдельно ниже.

## Анимационные инструкции

На карте ниже показаны жесты по шагам: перемещение, масштаб, меню, выделение, работа с виджетами и рисование. В каждой инструкции можно остановить анимацию, повторить её или замедлить.

![Анимационные инструкции по тач-интерфейсу](https://hitalama.158-160-195-195.ip.hyoo.ru/#!section=map/map=PyQy0aGj_ylh9R2NM/help=off/guest=sUzvXKHzYYgbCHS/shift_x=-488.144/shift_y=861.016/zoom=0.684/widget_props_tab=general)

[Открыть карту с инструкциями отдельно](https://hitalama.158-160-195-195.ip.hyoo.ru/#!section=map/map=PyQy0aGj_ylh9R2NM/help=off/guest=sUzvXKHzYYgbCHS/shift_x=-488.144/shift_y=861.016/zoom=0.684/widget_props_tab=general)

## Тачпад ноутбука

- **Один палец** перемещает указатель. Нажатие тачпада или настроенное в системе касание выполняет левый клик.
- **Вторичный клик** открывает контекстное меню. Обычно это нажатие двумя пальцами или в правом нижнем углу; точный жест зависит от настроек устройства.
- **Прокрутка двумя пальцами** над пустым фоном сдвигает карту по вертикали и горизонтали. Направление зависит от системной настройки прокрутки.
- **Ctrl + прокрутка двумя пальцами** меняет масштаб карты. Если браузер передаёт щипок как Ctrl-прокрутку, щипок тоже масштабирует карту.
- Для перетаскивания удерживайте основное нажатие и ведите палец. Жест «касание и удержание для перетаскивания» должен быть включён в системе, если вы хотите пользоваться им без физического нажатия.

Над содержимым виджета два пальца могут прокручивать редактор, таблицу или Markdown. Переведите указатель на пустой фон, чтобы двигать карту. Для перемещения виджета за содержимое удерживайте Ctrl — или другую назначенную клавишу перетаскивания. Для выделения рамкой нажмите на пустом фоне и протяните область, как левой кнопкой мыши.

В режиме рисования основное нажатие и движение рисуют выбранным инструментом. Пока активны карандаш или фигура, обычное движение с зажатым нажатием не является выделением: сначала выберите стрелку **Выбор**, если хотите выделять объекты.

## Если используется сенсорный экран

| Жест на карте | Действие |
| --- | --- |
| Провести одним пальцем по пустому фону | Переместить карту. |
| Развести или свести два пальца | Изменить масштаб; второй палец отменяет ожидание длинного касания и активную рамку выделения. |
| Короткий тап по виджету | Активировать виджет. Прокрутка его содержимого не должна перемещать сам объект. |
| Короткий тап по пустому фону | Снять выделение и закрыть контекстное меню. |
| Двойной тап по фону | Открыть меню карты. |
| Двойной тап по виджету | Открыть меню виджета, если тап приходится на его обычную область, а не на поле ввода, ссылку или кнопку. |
| Удерживать пустой фон около 0,6 секунды, затем вести палец | Начать новую рамку выделения нескольких объектов, даже если виджет или группа уже выбраны. |

Для группировки удержите пустой фон, обведите нужные объекты и отпустите палец. Нажмите кнопку группы над выделением. Если начать вести палец сразу, до срабатывания удержания, карта просто переместится. Длинный тап предназначен для рамки; контекстное меню вызывается двойным тапом.

Для перемещения виджета тяните специальную ручку перетаскивания. Для рисования выберите инструмент на панели и ведите палец от начальной точки; следующие штрихи и фигуры можно рисовать подряд. Рамка выделения по удержанию не включается, пока активен инструмент рисования. Чтобы вернуться к навигации и выделению, выберите стрелку.

[Управление картой](maps-navigation.md) · [Виджеты](maps-widgets.md) · [Группы](maps-layout.md)`),
			}
		}
	}
}
